import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { Department } from '../types';
import { departmentsApi, metricsApi } from '../services/api';
import { extractApiError } from '../types/apiError';

interface MetricFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  metricId?: number;
  defaultDepartmentName?: string;
}

const MetricForm: React.FC<MetricFormProps> = ({
  open,
  onClose,
  onSuccess,
  metricId,
  defaultDepartmentName,
}) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [unit, setUnit] = useState('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [sourceType, setSourceType] = useState<'manual' | 'api'>('manual');
  const [originalSourceType, setOriginalSourceType] = useState<'manual' | 'api' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDepartments();
    if (metricId) {
      loadMetric();
    } else {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on open/metricId change only
  }, [open, metricId]);

  useEffect(() => {
    if (!open || metricId || !defaultDepartmentName || departments.length === 0) {
      return;
    }
    if (departmentId !== '') {
      return;
    }
    const match = departments.find(
      (dept) => dept.name.toLowerCase() === defaultDepartmentName.toLowerCase()
    );
    if (match) {
      setDepartmentId(match.id);
    }
  }, [open, metricId, defaultDepartmentName, departments, departmentId]);

  const loadDepartments = async () => {
    try {
      const data = await departmentsApi.getAll();
      setDepartments(data);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadMetric = async () => {
    if (!metricId) return;
    try {
      const metric: any = await metricsApi.getById(metricId);
      setName(metric.name);
      setDescription(metric.description || '');
      setUnit(metric.unit || '');
      setDepartmentId(metric.department_id || '');
      const loadedSourceType = metric.source_type || 'manual';
      setSourceType(loadedSourceType);
      setOriginalSourceType(loadedSourceType);
    } catch (error) {
      console.error('Failed to load metric:', error);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setUnit('');
    setDepartmentId('');
    setSourceType('manual');
    setOriginalSourceType(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const finalSourceType = metricId && originalSourceType === 'api' ? 'api' : sourceType;

      const payload: any = {
        name,
        description: description || null,
        unit: unit || null,
        department_id: departmentId !== '' ? (departmentId as number) : null,
        metric_type: 'kpi' as const,
        source_type: finalSourceType,
      };

      if (metricId) {
        await metricsApi.update(metricId, payload);
      } else {
        await metricsApi.create(payload);
      }
      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      const status = err.response?.status;
      const message = extractApiError(err, 'Failed to save metric');
      if (status === 409) {
        setError(message + ' Do not use "Add Metric" for an existing metric—use the row menu (⋮) and "Edit metric" to update it.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{metricId ? 'Edit Metric' : 'Add New Metric'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Metric Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              fullWidth
              placeholder="e.g., $, %, units"
            />
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentId}
                label="Department"
                onChange={(e) => setDepartmentId(e.target.value as number | '')}
              >
                <MenuItem value="">No Department</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Source Type</InputLabel>
              <Select
                value={sourceType}
                label="Source Type"
                onChange={(e) => setSourceType(e.target.value as 'manual' | 'api')}
                disabled={!!(metricId && originalSourceType === 'api')}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="api">API</MenuItem>
              </Select>
            </FormControl>

            {error && (
              <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
                {error}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading || !name}>
            {metricId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MetricForm;
